// Catalog.ID Client — Certificate Badge Component
// ============================================================================
// Visual indicator for claim validation status.

import { createElement } from '@catalog-shell';

export type ValidationStatus = 'unvalidated' | 'pending' | 'validated' | 'expired' | 'revoked';

export interface CertificateBadgeConfig {
    status: ValidationStatus;
    expiresAt?: string | null;
    method?: string | null;
}

const STATUS_STYLES: Record<ValidationStatus, { bg: string; fg: string; label: string; icon: string }> = {
    unvalidated: { bg: 'rgba(128,128,128,0.15)', fg: '#888', label: 'Unvalidated', icon: '○' },
    pending:     { bg: 'rgba(100,149,237,0.15)', fg: 'cornflowerblue', label: 'Pending', icon: '◌' },
    validated:   { bg: 'rgba(80,200,80,0.15)', fg: '#5c5', label: 'Validated', icon: '✓' },
    expired:     { bg: 'rgba(220,150,50,0.15)', fg: '#ea4', label: 'Expired', icon: '⏱' },
    revoked:     { bg: 'rgba(220,50,50,0.15)', fg: '#e55', label: 'Revoked', icon: '✕' },
};

export function createCertificateBadge(config: CertificateBadgeConfig): HTMLElement {
    const style = STATUS_STYLES[config.status] || STATUS_STYLES.unvalidated;

    const badge = createElement('span', {
        className: 'cid-cert-badge',
        style: { background: style.bg, color: style.fg, border: `1px solid ${style.fg}33` },
    });

    badge.appendChild(createElement('span', {
        className: 'cid-cert-badge-icon',
        textContent: style.icon,
    }));

    badge.appendChild(createElement('span', {
        className: 'cid-cert-badge-label',
        textContent: style.label,
    }));

    if (config.expiresAt && config.status === 'validated') {
        const expires = new Date(config.expiresAt);
        const now = new Date();
        const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        if (daysLeft <= 30) {
            badge.appendChild(createElement('span', {
                className: 'cid-cert-badge-expiry',
                textContent: daysLeft > 0 ? ` (${daysLeft}d)` : ' (expiring)',
                style: { color: daysLeft <= 7 ? '#ea4' : style.fg },
            }));
        }
    }

    if (config.method) {
        badge.title = `Validated via ${config.method}${config.expiresAt ? ` — expires ${new Date(config.expiresAt).toLocaleDateString()}` : ''}`;
    }

    return badge;
}

/** Derive ValidationStatus from server claim data. */
export function deriveValidationStatus(
    serverStatus: string,
    certificates: Array<{ isActive: boolean; expiresAt: string | null }>,
    revokedAt: string | null,
): ValidationStatus {
    if (revokedAt) return 'revoked';
    if (serverStatus === 'pending') return 'pending';
    if (serverStatus === 'validated') {
        const activeCert = certificates.find(c => c.isActive);
        if (activeCert) {
            // null expiresAt = no expiry (e.g. registry_signing_key per WP §8.1).
            if (!activeCert.expiresAt) return 'validated';
            const expires = new Date(activeCert.expiresAt);
            if (expires < new Date()) return 'expired';
            return 'validated';
        }
        return 'expired';
    }
    return 'unvalidated';
}
